import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Copy,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Globe,
  Plus,
  Briefcase,
  QrCode,
  Download,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { API } from "@/context/AuthContext";

const PUBLIC_BASE = typeof window !== "undefined" ? window.location.origin : "";

const CHANNEL_PRESETS = [
  { id: "whatsapp", label: "WhatsApp", source: "whatsapp", medium: "social", color: "#25D366" },
  { id: "linkedin", label: "LinkedIn", source: "linkedin", medium: "social", color: "#0A66C2" },
  { id: "instagram", label: "Instagram", source: "instagram", medium: "social", color: "#E4405F" },
  { id: "facebook", label: "Facebook", source: "facebook", medium: "social", color: "#1877F2" },
  { id: "email", label: "E-mail", source: "email", medium: "email", color: "#64748b" },
  { id: "qr_print", label: "Banner / QR impresso", source: "qr", medium: "print", color: "#0f172a" },
];

const CompanyCareerLinksPanel = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState("");
  const [qrOrg, setQrOrg] = useState(null);
  const [activeChannel, setActiveChannel] = useState(CHANNEL_PRESETS[0]);
  const qrCanvasRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/registry/organizations`);
        setOrgs(res.data || []);
      } catch {
        setOrgs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const publicUrlFor = (slug) =>
    slug ? `${PUBLIC_BASE}/carreiras/org/${slug}` : "";

  // Build a /share/ URL with the chosen channel's UTM tracking.
  // Result: `/api/public/share/orgs/{slug}?utm_source=whatsapp&utm_medium=social&utm_campaign=carreiras-{slug}`
  const shareUrlFor = (slug, channel = activeChannel) => {
    if (!slug) return "";
    const base = `${PUBLIC_BASE}/api/public/share/orgs/${slug}`;
    if (!channel) return base;
    const params = new URLSearchParams({
      utm_source: channel.source,
      utm_medium: channel.medium,
      utm_campaign: `carreiras-${slug}`,
    });
    return `${base}?${params.toString()}`;
  };

  const handleCopy = async (slug) => {
    const url = shareUrlFor(slug);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      toast.success("Link copiado! Preview rico no WhatsApp/LinkedIn.");
      setTimeout(() => setCopiedSlug(""), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const handleDownloadQr = () => {
    if (!qrOrg || !qrCanvasRef.current) return;
    const canvas = qrCanvasRef.current.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${activeChannel.id}-${qrOrg.slug || "empresa"}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePrintQr = () => {
    if (!qrOrg || !qrCanvasRef.current) return;
    const canvas = qrCanvasRef.current.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank", "width=480,height=640");
    if (!win) {
      toast.error("Habilite pop-ups para imprimir");
      return;
    }
    const url = shareUrlFor(qrOrg.slug);

    // Build the print preview using DOM APIs in the popup window (no document.write).
    // User-controlled strings (trade_name, url) go through `.textContent` so they
    // can't inject markup or scripts.
    const doc = win.document;
    doc.open();
    doc.close();
    doc.documentElement.lang = "pt-BR";
    doc.head.innerHTML = "";
    const charset = doc.createElement("meta");
    charset.setAttribute("charset", "utf-8");
    doc.head.appendChild(charset);
    const title = doc.createElement("title");
    title.textContent = `QR Vagas — ${qrOrg.trade_name}`;
    doc.head.appendChild(title);
    const style = doc.createElement("style");
    style.textContent = `
      * { box-sizing: border-box; }
      body { font-family: 'Inter', -apple-system, system-ui, sans-serif; margin: 0; padding: 48px 32px; text-align: center; color: #0f172a; }
      h1 { font-size: 22px; margin: 0 0 4px; }
      .sub { color: #64748b; font-size: 13px; margin-bottom: 28px; }
      img { max-width: 360px; width: 100%; height: auto; border: 1px solid #e2e8f0; padding: 12px; border-radius: 12px; }
      .url { margin-top: 20px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; color: #475569; word-break: break-all; }
      .tag { display: inline-block; margin-top: 24px; padding: 6px 12px; background: #eff6ff; color: #1d70d6; border-radius: 999px; font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; }
    `;
    doc.head.appendChild(style);

    doc.body.innerHTML = "";
    const h1 = doc.createElement("h1");
    h1.textContent = `Vagas em ${qrOrg.trade_name}`;
    doc.body.appendChild(h1);

    const sub = doc.createElement("div");
    sub.className = "sub";
    sub.textContent = "Aponte a câmera do celular para se candidatar.";
    doc.body.appendChild(sub);

    const img = doc.createElement("img");
    img.src = dataUrl;
    img.alt = "QR Code";
    doc.body.appendChild(img);

    const urlEl = doc.createElement("div");
    urlEl.className = "url";
    urlEl.textContent = url;
    doc.body.appendChild(urlEl);

    const tag = doc.createElement("div");
    tag.className = "tag";
    tag.textContent = "GridRecruiter";
    doc.body.appendChild(tag);

    // Trigger print after the image actually loaded
    img.onload = () => {
      win.setTimeout(() => win.print(), 300);
    };
  };

  if (loading) {
    return (
      <div
        data-testid="company-links-loading"
        className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-2 text-sm text-slate-500"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando links das empresas…
      </div>
    );
  }

  return (
    <section
      data-testid="company-career-links-panel"
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-blue-50 text-brand-blue flex items-center justify-center">
            <Globe className="w-4 h-4" />
          </div>
          <div>
            <h2 className="heading text-base font-semibold text-slate-900 leading-tight">
              Links públicos por empresa
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Cada empresa cadastrada tem seu próprio banco de vagas.
              Compartilhe o link específico com os candidatos.
            </p>
          </div>
        </div>
        <Link
          to="/cadastros"
          data-testid="go-to-companies-registry"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-blue hover:text-brand-blue-dark"
        >
          <Plus className="w-3.5 h-3.5" />
          Cadastrar nova empresa
        </Link>
      </div>

      {orgs.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
          <Building2 className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">
            Nenhuma empresa cadastrada. Cadastre em{" "}
            <Link to="/cadastros" className="text-brand-blue underline">
              Cadastros → Empresas
            </Link>{" "}
            para gerar um link de carreiras dedicado.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {orgs.map((o) => {
            const url = publicUrlFor(o.slug);
            const isCopied = copiedSlug === o.slug;
            return (
              <li
                key={o.id}
                data-testid={`company-link-row-${o.id}`}
                className="py-3 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center overflow-hidden">
                    {o.logo_url ? (
                      <img
                        src={o.logo_url}
                        alt={`Logo ${o.trade_name}`}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate text-sm">
                      {o.trade_name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {o.jobs_count ?? 0} vaga{(o.jobs_count ?? 0) !== 1 ? "s" : ""}
                      </span>
                      {o.city && <span>· {o.city}/{o.state}</span>}
                    </div>
                    {url && (
                      <div className="mt-1">
                        <code className="text-[10px] font-mono text-slate-500 truncate block max-w-full">
                          {url}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopy(o.slug)}
                    disabled={!url}
                    data-testid={`copy-company-link-${o.id}`}
                    className="h-8 text-xs"
                  >
                    {isCopied ? (
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 mr-1" />
                    )}
                    Copiar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setQrOrg(o)}
                    disabled={!url}
                    data-testid={`qr-company-link-${o.id}`}
                    className="h-8 text-xs"
                    title="Gerar QR Code"
                  >
                    <QrCode className="w-3.5 h-3.5 mr-1" />
                    QR
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                    disabled={!url}
                    data-testid={`open-company-link-${o.id}`}
                    className="h-8 text-xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                    Abrir
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* QR Code dialog */}
      <Dialog open={!!qrOrg} onOpenChange={(open) => !open && setQrOrg(null)}>
        <DialogContent data-testid="qr-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading text-lg">
              QR Code · {qrOrg?.trade_name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Aponte a câmera do celular para abrir a página de vagas desta empresa.
              Ótimo para banners, flyers e balcões de RH.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 pt-2">
            {qrOrg && (
              <>
                {/* Channel selector — drives UTMs embedded in the QR / link */}
                <div className="w-full">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                    Canal de divulgação
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-testid="channel-chips">
                    {CHANNEL_PRESETS.map((ch) => {
                      const isActive = activeChannel.id === ch.id;
                      return (
                        <button
                          type="button"
                          key={ch.id}
                          onClick={() => setActiveChannel(ch)}
                          data-testid={`channel-chip-${ch.id}`}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                            isActive
                              ? "border-transparent text-white"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                          }`}
                          style={isActive ? { backgroundColor: ch.color } : undefined}
                        >
                          {ch.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1.5">
                    Adicionamos automaticamente <code>?utm_source={activeChannel.source}</code>{" "}
                    para você medir o desempenho de cada canal no Dashboard.
                  </p>
                </div>

                {/* Visible SVG for crisp rendering on screen */}
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <QRCodeSVG
                    value={shareUrlFor(qrOrg.slug)}
                    size={232}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                    imageSettings={
                      qrOrg.logo_url
                        ? {
                            src: qrOrg.logo_url,
                            height: 48,
                            width: 48,
                            excavate: true,
                          }
                        : undefined
                    }
                    data-testid="qr-svg"
                  />
                </div>
                {/* Hidden canvas used only for PNG download/print */}
                <div ref={qrCanvasRef} style={{ position: "absolute", left: "-9999px" }}>
                  <QRCodeCanvas
                    value={shareUrlFor(qrOrg.slug)}
                    size={720}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                    imageSettings={
                      qrOrg.logo_url
                        ? {
                            src: qrOrg.logo_url,
                            height: 140,
                            width: 140,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>
                <div className="w-full">
                  <code className="block bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-[11px] font-mono text-slate-600 truncate">
                    {shareUrlFor(qrOrg.slug)}
                  </code>
                  <p className="text-[10px] text-slate-500 mt-1.5">
                    Este link gera preview com imagem e descrição no
                    WhatsApp, LinkedIn e Facebook. Humanos são redirecionados
                    para a página de vagas imediatamente.
                  </p>
                </div>
                <div className="flex w-full gap-2">
                  <Button
                    onClick={() => handleCopy(qrOrg.slug)}
                    variant="outline"
                    className="flex-1"
                    data-testid="qr-copy-link-btn"
                  >
                    {copiedSlug === qrOrg.slug ? (
                      <CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 mr-1.5" />
                    )}
                    Copiar link
                  </Button>
                  <Button
                    onClick={handleDownloadQr}
                    className="flex-1 bg-brand-blue hover:bg-brand-blue-dark text-white"
                    data-testid="qr-download-btn"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Baixar PNG
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handlePrintQr}
                    data-testid="qr-print-btn"
                    className="flex-1"
                  >
                    <Printer className="w-4 h-4 mr-1.5" />
                    Imprimir
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CompanyCareerLinksPanel;
